import React, { useEffect, useMemo, useState } from 'react';
import { getSettings, getSumUpReaderStatus, pairSumUpReader, updateSettings } from '../../../lib/api';
import { getPaymentMethodLabel, isGpTomConfigured, isSumUpConfigured, normalizePaymentMethods } from '../../../lib/paymentMethods';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Banknote, CreditCard, Loader2, RefreshCw, Save } from 'lucide-react';
import { toast } from 'sonner';

const METHOD_ICONS = {
  cash: Banknote,
  card: CreditCard,
  card_sumup: CreditCard,
  card_manual: CreditCard,
  bank_transfer: Banknote,
  customer_card: CreditCard,
  internal: Banknote,
};

const TERMINAL_LABELS = {
  cash: 'Kassa',
  gptom_cloud: 'GP tom App',
  sumup_solo_cloud: 'SumUp Solo',
  manual: 'Separates Geraet',
  bank_transfer: 'Buchung',
  customer_card: 'Kundenkonto',
  internal: 'Intern',
};

function MethodRow({ method, onToggle }) {
  const Icon = METHOD_ICONS[method.id] || CreditCard;
  const statusTone = method.enabled !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500';
  const terminalKey = method.terminal_type || method.id;

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${statusTone}`}>
            <Icon className="h-4 w-4" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">{method.name}</p>
            <p className="text-xs text-slate-400">{getPaymentMethodLabel(method.id)}</p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Badge variant="outline">{TERMINAL_LABELS[terminalKey] || terminalKey}</Badge>
          {method.provider === 'gptom' ? <Badge className="bg-blue-600 text-white hover:bg-blue-600">API</Badge> : null}
          {method.provider === 'sumup' ? <Badge className="bg-sky-700 text-white hover:bg-sky-700">Cloud</Badge> : null}
          {method.provider === 'manual_terminal' ? <Badge className="bg-slate-700 text-white hover:bg-slate-700">Manuell</Badge> : null}
          {method.provider === 'customer_card' ? <Badge className="bg-cyan-700 text-white hover:bg-cyan-700">Kunde</Badge> : null}
        </div>
      </TableCell>
      <TableCell className="text-sm text-slate-500">{method.description}</TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-3">
          <span className="text-xs font-medium text-slate-500">{method.enabled !== false ? 'Aktiv' : 'Inaktiv'}</span>
          <Switch
            checked={method.enabled !== false}
            onCheckedChange={(checked) => onToggle(method.id, checked)}
            disabled={method.id === 'cash'}
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function AdminPaymentMethods() {
  const [settings, setSettings] = useState(null);
  const [methods, setMethods] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [sumUpPairingCode, setSumUpPairingCode] = useState('');
  const [sumUpReaderAlias, setSumUpReaderAlias] = useState('FiscalPOS SumUp Reader');
  const [sumUpReaderStatus, setSumUpReaderStatus] = useState(null);
  const [isPairingReader, setIsPairingReader] = useState(false);
  const [isCheckingReader, setIsCheckingReader] = useState(false);

  const gpTomReady = useMemo(() => isGpTomConfigured(settings), [settings]);
  const sumUpReady = useMemo(() => isSumUpConfigured(settings), [settings]);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const liveSettings = await getSettings();
      setSettings(liveSettings);
      setMethods(normalizePaymentMethods(liveSettings?.payment_methods));
      setSumUpReaderAlias(liveSettings?.sumup_reader_name || 'FiscalPOS SumUp Reader');
    } catch (error) {
      console.error('Error loading payment settings:', error);
      toast.error('Fehler beim Laden der Zahlungsarten');
    } finally {
      setIsLoading(false);
    }
  };

  const updateMethod = (methodId, enabled) => {
    setMethods((current) => current.map((method) => (
      method.id === methodId ? { ...method, enabled } : method
    )));
  };

  const updateField = (field, value) => {
    setSettings((current) => ({ ...current, [field]: value }));
  };

  const handleSave = async () => {
    if (!settings) return;

    setIsSaving(true);
    try {
      await updateSettings({
        ...settings,
        payment_methods: methods,
      });
      toast.success('Zahlungsarten gespeichert');
    } catch (error) {
      console.error('Error saving payment settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePairSumUpReader = async () => {
    if (!sumUpPairingCode.trim()) {
      toast.error('Bitte zuerst den Pairing Code vom SumUp Solo eingeben');
      return;
    }

    setIsPairingReader(true);
    try {
      const paired = await pairSumUpReader({
        pairing_code: sumUpPairingCode.trim(),
        name: sumUpReaderAlias.trim() || 'FiscalPOS SumUp Reader',
      });
      setSettings((current) => ({
        ...(current || {}),
        sumup_reader_id: paired?.id || current?.sumup_reader_id || '',
        sumup_reader_name: paired?.name || sumUpReaderAlias.trim(),
      }));
      setSumUpReaderAlias(paired?.name || sumUpReaderAlias.trim() || 'FiscalPOS SumUp Reader');
      setSumUpPairingCode('');
      toast.success(`SumUp Reader gekoppelt: ${paired?.name || paired?.id}`);
    } catch (error) {
      console.error('Error pairing SumUp reader:', error);
      toast.error(error?.response?.data?.detail || 'SumUp Reader konnte nicht gekoppelt werden');
    } finally {
      setIsPairingReader(false);
    }
  };

  const handleCheckSumUpReader = async () => {
    setIsCheckingReader(true);
    try {
      const status = await getSumUpReaderStatus();
      setSumUpReaderStatus(status);
      toast.success('SumUp Reader erreichbar');
    } catch (error) {
      console.error('Error checking SumUp reader:', error);
      toast.error(error?.response?.data?.detail || 'SumUp Reader-Status konnte nicht geladen werden');
    } finally {
      setIsCheckingReader(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1a2f38]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Zahlungsarten</h1>
          <p className="text-slate-500">Nur produktiv geplante Zahlungsarten fuer die Kassa.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadSettings}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Aktualisieren
          </Button>
          <Button onClick={handleSave} disabled={isSaving} className="bg-[#1a2f38] hover:bg-[#2a4048]">
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Speichern
          </Button>
        </div>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Zahlungsart</TableHead>
                <TableHead>Abwicklung</TableHead>
                <TableHead>Beschreibung</TableHead>
                <TableHead className="text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {methods.map((method) => (
                <MethodRow key={method.id} method={method} onToggle={updateMethod} />
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="space-y-6 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-[#1a2f38]">GP tom Kartenzahlung</h2>
              <p className="text-sm text-slate-500">Terminal am Smartphone oder Tablet fuer die browserbasierte Kassa.</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={settings?.gp_enabled ? 'bg-emerald-600 text-white hover:bg-emerald-600' : 'bg-slate-200 text-slate-700 hover:bg-slate-200'}>
                {settings?.gp_enabled ? 'Integration aktiv' : 'Integration aus'}
              </Badge>
              <Badge className={gpTomReady ? 'bg-blue-600 text-white hover:bg-blue-600' : 'bg-amber-100 text-amber-700 hover:bg-amber-100'}>
                {gpTomReady ? 'Konfiguriert' : 'Unvollstaendig'}
              </Badge>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div>
              <p className="font-medium text-slate-900">GP tom aktivieren</p>
              <p className="text-sm text-slate-500">Aktiviert den Kartenzahlungsbutton fuer das Smartphone-Terminal.</p>
            </div>
            <Switch checked={Boolean(settings?.gp_enabled)} onCheckedChange={(checked) => updateField('gp_enabled', checked)} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="gp-tom-api-url">Cloud API URL</Label>
              <Input
                id="gp-tom-api-url"
                value={settings?.gp_tom_api_url || 'https://cloud-api.gptom.com'}
                onChange={(event) => updateField('gp_tom_api_url', event.target.value)}
                placeholder="https://cloud-api.gptom.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gp-tom-api-key">Cloud API Key</Label>
              <Input
                id="gp-tom-api-key"
                value={settings?.gp_tom_api_key || ''}
                onChange={(event) => updateField('gp_tom_api_key', event.target.value)}
                placeholder="API Key aus GP tom"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gp-tom-username">GP tom Benutzername</Label>
              <Input
                id="gp-tom-username"
                value={settings?.gp_tom_username || ''}
                onChange={(event) => updateField('gp_tom_username', event.target.value)}
                placeholder="Login wie in GP tom"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gp-tom-password">GP tom Passwort</Label>
              <Input
                id="gp-tom-password"
                type="password"
                value={settings?.gp_tom_password || ''}
                onChange={(event) => updateField('gp_tom_password', event.target.value)}
                placeholder="Passwort fuer GP tom"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gp-tom-tid">TID</Label>
              <Input
                id="gp-tom-tid"
                value={settings?.gp_tom_tid || ''}
                onChange={(event) => updateField('gp_tom_tid', event.target.value)}
                placeholder="Terminal-ID aus GP tom"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gp-tom-initiator">Initiator (optional)</Label>
              <Input
                id="gp-tom-initiator"
                value={settings?.gp_tom_initiator || ''}
                onChange={(event) => updateField('gp_tom_initiator', event.target.value)}
                placeholder="Leer = POS_001 oder KASSA001"
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div>
              <p className="font-medium text-slate-900">Beleg auf GP tom drucken</p>
              <p className="text-sm text-slate-500">Nur aktivieren, wenn am Smartphone oder Tablet ein Drucker angebunden ist.</p>
            </div>
            <Switch checked={Boolean(settings?.gp_tom_print_receipt)} onCheckedChange={(checked) => updateField('gp_tom_print_receipt', checked)} />
          </div>

          <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            Die Kassa startet einen GP tom Task, pollt den Taskstatus und holt danach das finale Zahlungsergebnis aus der Cloud API.
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="space-y-6 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-[#1a2f38]">SumUp Kartenzahlung</h2>
              <p className="text-sm text-slate-500">Cloud-API fuer SumUp Solo in der browserbasierten Kassa.</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={settings?.sumup_enabled ? 'bg-emerald-600 text-white hover:bg-emerald-600' : 'bg-slate-200 text-slate-700 hover:bg-slate-200'}>
                {settings?.sumup_enabled ? 'Integration aktiv' : 'Integration aus'}
              </Badge>
              <Badge className={sumUpReady ? 'bg-sky-700 text-white hover:bg-sky-700' : 'bg-amber-100 text-amber-700 hover:bg-amber-100'}>
                {sumUpReady ? 'Konfiguriert' : 'Unvollstaendig'}
              </Badge>
            </div>
          </div>

          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Echtes SumUp Tap to Pay braucht laut offizieller SumUp-Dokumentation eine native iOS- oder Android-App mit SDK.
            Diese Web-Kassa integriert deshalb den offiziell webtauglichen SumUp-Cloud-Flow mit Solo Reader.
          </div>

          <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div>
              <p className="font-medium text-slate-900">SumUp aktivieren</p>
              <p className="text-sm text-slate-500">Aktiviert SumUp Solo als zweite integrierte Kartenart in der POS.</p>
            </div>
            <Switch checked={Boolean(settings?.sumup_enabled)} onCheckedChange={(checked) => updateField('sumup_enabled', checked)} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="sumup-api-url">API Base URL</Label>
              <Input
                id="sumup-api-url"
                value={settings?.sumup_api_url || 'https://api.sumup.com'}
                onChange={(event) => updateField('sumup_api_url', event.target.value)}
                placeholder="https://api.sumup.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sumup-api-key">Secret API Key</Label>
              <Input
                id="sumup-api-key"
                type="password"
                value={settings?.sumup_api_key || ''}
                onChange={(event) => updateField('sumup_api_key', event.target.value)}
                placeholder="sk_live_... oder sk_test_..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sumup-merchant-code">Merchant Code</Label>
              <Input
                id="sumup-merchant-code"
                value={settings?.sumup_merchant_code || ''}
                onChange={(event) => updateField('sumup_merchant_code', event.target.value)}
                placeholder="MH4H92C7"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sumup-affiliate-key">Affiliate Key</Label>
              <Input
                id="sumup-affiliate-key"
                value={settings?.sumup_affiliate_key || ''}
                onChange={(event) => updateField('sumup_affiliate_key', event.target.value)}
                placeholder="Affiliate Key aus SumUp"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sumup-affiliate-app-id">Affiliate App ID</Label>
              <Input
                id="sumup-affiliate-app-id"
                value={settings?.sumup_affiliate_app_id || ''}
                onChange={(event) => updateField('sumup_affiliate_app_id', event.target.value)}
                placeholder="com.fiscalpos.web"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sumup-reader-id">Reader ID</Label>
              <Input
                id="sumup-reader-id"
                value={settings?.sumup_reader_id || ''}
                onChange={(event) => updateField('sumup_reader_id', event.target.value)}
                placeholder="rdr_..."
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="sumup-reader-name">Reader Name</Label>
              <Input
                id="sumup-reader-name"
                value={settings?.sumup_reader_name || ''}
                onChange={(event) => {
                  updateField('sumup_reader_name', event.target.value);
                  setSumUpReaderAlias(event.target.value);
                }}
                placeholder="Frontdesk"
              />
            </div>
          </div>

          <div className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-[1fr_1fr_auto]">
            <div className="space-y-2">
              <Label htmlFor="sumup-pairing-code">Pairing Code</Label>
              <Input
                id="sumup-pairing-code"
                value={sumUpPairingCode}
                onChange={(event) => setSumUpPairingCode(event.target.value.toUpperCase())}
                placeholder="4WLFDSBF"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sumup-pairing-name">Reader Alias beim Pairing</Label>
              <Input
                id="sumup-pairing-name"
                value={sumUpReaderAlias}
                onChange={(event) => setSumUpReaderAlias(event.target.value)}
                placeholder="Frontdesk"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button
                type="button"
                onClick={handlePairSumUpReader}
                disabled={isPairingReader}
                className="bg-sky-700 hover:bg-sky-800"
              >
                {isPairingReader ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Reader koppeln
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
            <div className="flex-1">
              <p className="font-medium text-slate-900">Aktueller Reader</p>
              <p className="text-sm text-slate-500">
                {settings?.sumup_reader_name || 'Noch kein Reader gespeichert'}
                {settings?.sumup_reader_id ? ` · ${settings.sumup_reader_id}` : ''}
              </p>
            </div>
            <Button type="button" variant="outline" onClick={handleCheckSumUpReader} disabled={isCheckingReader}>
              {isCheckingReader ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Reader testen
            </Button>
          </div>

          {sumUpReaderStatus ? (
            <div className="rounded-2xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-950">
              <p className="font-medium">Reader Status: {sumUpReaderStatus.reader_status || 'Unbekannt'}</p>
              <p>Aktueller Screen: {sumUpReaderStatus.reader_state || 'Unbekannt'}</p>
              {sumUpReaderStatus.connection_type ? <p>Verbindung: {sumUpReaderStatus.connection_type}</p> : null}
              {sumUpReaderStatus.battery_level != null ? <p>Batterie: {sumUpReaderStatus.battery_level}%</p> : null}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
